import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { useDialog } from '../components/Dialog';
import StatusBadge from '../components/StatusBadge';
import {
  BackLink,
  ChatThread,
  Field,
  MapLink,
  Timeline,
  usePerson,
} from '../components/Detail';
import { adminCancelOrderForce } from '../lib/api';
import {
  formatDateTime,
  money,
  orderTotal,
  shortAddress,
} from '../lib/format';

type Doc = Record<string, unknown>;

function loc(o: Doc, key: string) {
  const l = o[key] as Doc | undefined;
  return {
    address: typeof l?.address === 'string' ? l.address : '',
    lat: typeof l?.latitude === 'number' ? l.latitude : undefined,
    lng: typeof l?.longitude === 'number' ? l.longitude : undefined,
  };
}

const BREAKDOWN_ROWS: { key: string; label: string }[] = [
  { key: 'deliveryFee', label: 'Delivery fee' },
  { key: 'vehicleSurcharge', label: 'Vehicle surcharge' },
  { key: 'equipmentCost', label: 'Equipment' },
  { key: 'flexibleDiscount', label: 'Flexible discount' },
  { key: 'platformFee', label: 'Platform fee' },
  { key: 'subtotal', label: 'Subtotal' },
  { key: 'tip', label: 'Tip' },
  { key: 'total', label: 'Total' },
];

export default function OrderDetailPage() {
  const { id = '' } = useParams();
  // undefined = loading, null = not found
  const [order, setOrder] = useState<Doc | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { prompt, dialog } = useDialog();

  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(
      doc(db, 'orders', id),
      (snap) =>
        setOrder(
          snap.exists() ? { ...(snap.data() as Doc), id: snap.id } : null
        ),
      (e) => setError(`Failed to load order: ${e.message}`)
    );
    return unsub;
  }, [id]);

  const customer = usePerson(order?.customerId as string | undefined);
  const driver = usePerson(order?.driverId as string | undefined);

  const onForceCancel = async () => {
    const reason = await prompt({
      title: 'Force-cancel order',
      message: `Force-cancel order ${id}? Provide a reason for the audit log.`,
      placeholder: 'Reason',
      required: true,
      destructive: true,
      confirmLabel: 'Cancel order',
      cancelLabel: 'Keep order',
    });
    if (!reason) return;
    setBusy(true);
    try {
      await adminCancelOrderForce({ orderId: id, reason });
    } catch (e) {
      setError(`Cancel failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  };

  if (order === undefined) {
    return (
      <>
        <BackLink to="/orders" label="Back to orders" />
        <p className="text-sm text-slate-500">Loading order…</p>
      </>
    );
  }
  if (order === null) {
    return (
      <>
        <BackLink to="/orders" label="Back to orders" />
        <Card>
          <p className="text-sm text-slate-600">
            Order <span className="font-mono">{id}</span> was not found.
          </p>
        </Card>
      </>
    );
  }

  const status = order.status as string | undefined;
  const pickup = loc(order, 'pickupLocation');
  const delivery = loc(order, 'deliveryLocation');
  const items = (order.items as Doc[] | undefined) ?? [];
  const history = ((order.statusHistory as Doc[] | undefined) ?? []).map(
    (h) => ({
      status: h.status as string | undefined,
      timestamp: h.timestamp,
      notes: h.notes as string | undefined,
      updatedBy: h.updatedBy as string | undefined,
    })
  );
  const breakdown = order.pricingBreakdown as Doc | undefined;
  const payment = order.paymentInfo as Doc | undefined;
  const driverLoc = order.driverLocation as Doc | undefined;
  const photos = (order.photos as string[] | undefined) ?? [];
  const equipment = (order.equipmentRequired as string[] | undefined) ?? [];
  const driverPct =
    typeof breakdown?.driverPercentage === 'number'
      ? breakdown.driverPercentage
      : undefined;
  const canCancel = status !== 'cancelled' && status !== 'delivered';

  return (
    <>
      <BackLink to="/orders" label="Back to orders" />
      <PageHeader
        title={shortAddress(pickup.address) || 'Order'}
        subtitle={`Order ${id}`}
      >
        {canCancel && (
          <button
            onClick={onForceCancel}
            disabled={busy}
            className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-60"
          >
            Force cancel
          </button>
        )}
      </PageHeader>
      {dialog}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Status & timeline">
          <div className="mb-3 flex items-center justify-between">
            <StatusBadge status={status} />
            {order.isUrgent === true && (
              <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-600/20">
                Urgent
              </span>
            )}
          </div>
          <Field label="Created" value={formatDateTime(order.createdAt)} />
          <Field label="Updated" value={formatDateTime(order.updatedAt)} />
          {order.scheduledDate != null && (
            <Field
              label="Scheduled"
              value={`${formatDateTime(order.scheduledDate)}${
                order.scheduledTimeWindow
                  ? ` · ${order.scheduledTimeWindow}`
                  : ''
              }`}
            />
          )}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <Timeline entries={history} />
          </div>
        </Card>

        <Card title="Route">
          <Field
            label="Pickup"
            value={
              <MapLink lat={pickup.lat} lng={pickup.lng}>
                {pickup.address || '—'}
              </MapLink>
            }
          />
          <Field
            label="Drop-off"
            value={
              <MapLink lat={delivery.lat} lng={delivery.lng}>
                {delivery.address || '—'}
              </MapLink>
            }
          />
          {driverLoc && (
            <Field
              label="Driver location"
              value={
                <MapLink
                  lat={driverLoc.latitude as number | undefined}
                  lng={driverLoc.longitude as number | undefined}
                >
                  View on map
                </MapLink>
              }
            />
          )}
        </Card>

        <Card title="Payment">
          <Field label="Order total" value={money(orderTotal(order))} />
          {breakdown &&
            BREAKDOWN_ROWS.map(({ key, label }) =>
              typeof breakdown[key] === 'number' ? (
                <Field
                  key={key}
                  label={label}
                  value={money(breakdown[key] as number)}
                />
              ) : null
            )}
          {driverPct != null && (
            <Field
              label="Driver share"
              value={`${Math.round(driverPct * 100)}%`}
            />
          )}
          {payment && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <Field
                label="Payment status"
                value={<StatusBadge status={payment.status as string} />}
              />
              <Field label="Method" value={payment.method as string} />
              <Field
                label="Processed"
                value={formatDateTime(payment.processedAt)}
              />
              <Field
                label="Transaction"
                value={payment.transactionId as string}
                mono
              />
              {typeof payment.receiptUrl === 'string' && payment.receiptUrl && (
                <Field
                  label="Receipt"
                  value={
                    <a
                      href={payment.receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-600 hover:underline"
                    >
                      View receipt
                    </a>
                  }
                />
              )}
            </div>
          )}
        </Card>

        <Card title="People">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Customer
          </div>
          <Field
            label="Name"
            value={
              order.customerId ? (
                <Link
                  to={`/customers?q=${order.customerId}`}
                  className="text-brand-600 hover:underline"
                >
                  {customer?.name ?? '…'}
                </Link>
              ) : (
                customer?.name
              )
            }
          />
          <Field label="Email" value={customer?.email} />
          <Field label="Phone" value={customer?.phone} />
          <div className="mt-3 border-t border-slate-100 pt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Driver
          </div>
          {order.driverId ? (
            <>
              <Field
                label="Name"
                value={
                  <Link
                    to={`/drivers?q=${order.driverId}`}
                    className="text-brand-600 hover:underline"
                  >
                    {driver?.name ?? '…'}
                  </Link>
                }
              />
              <Field label="Email" value={driver?.email} />
              <Field label="Phone" value={driver?.phone} />
            </>
          ) : (
            <p className="py-1.5 text-sm text-slate-400">Not yet assigned.</p>
          )}
        </Card>

        <Card title={`Items (${items.length})`}>
          {items.length === 0 ? (
            <p className="text-sm text-slate-400">No items listed.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it, i) => (
                <li key={i} className="flex items-start justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-800">
                      {(it.name as string) || 'Item'}
                    </div>
                    {typeof it.description === 'string' && it.description && (
                      <div className="truncate text-xs text-slate-500">
                        {it.description}
                      </div>
                    )}
                    <div className="mt-0.5 flex gap-1.5">
                      {it.isFragile === true && (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                          Fragile
                        </span>
                      )}
                      {it.isHazardous === true && (
                        <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                          Hazardous
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-xs text-slate-500">
                    <div>
                      ×{(it.quantity as number) ?? 1}
                      {it.unit ? ` ${it.unit}` : ''}
                    </div>
                    {typeof it.weight === 'number' && it.weight > 0 && (
                      <div>{it.weight} lb</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Details">
          <Field
            label="Vehicle required"
            value={order.vehicleTypeRequired as string}
          />
          <Field
            label="Equipment"
            value={equipment.length ? equipment.join(', ') : undefined}
          />
          <Field
            label="Total weight"
            value={
              typeof order.totalWeight === 'number' && order.totalWeight > 0
                ? `${order.totalWeight} lb`
                : undefined
            }
          />
          <Field
            label="Signature"
            value={order.requiresSignature === true ? 'Required' : 'Not required'}
          />
          <Field
            label="Instructions"
            value={order.specialInstructions as string}
          />
        </Card>
      </div>

      {photos.length > 0 && (
        <Card title="Proof of delivery">
          <div className="flex flex-wrap gap-3">
            {photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt={`Proof ${i + 1}`}
                  className="h-28 w-28 rounded border border-slate-200 object-cover"
                />
              </a>
            ))}
          </div>
        </Card>
      )}

      <Card title="Messages">
        <ChatThread orderId={id} />
      </Card>
    </>
  );
}
